import { Injectable } from '@angular/core';

/**
 * Service for managing data in browser's session storage.
 * This is a generic helper service that can be used to store and retrieve
 * any type of data in session storage.
 *
 * Storage is automatically cleared on logout or session expiry.
 */
@Injectable({
  providedIn: 'root'
})
export class StorageHelperService {
  /**
   * Saves data to session storage with the specified key
   * @param key The storage key
   * @param value The value to save (will be JSON stringified)
   */
  setItem<T>(key: string, value: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to save item to session storage (key: ${key}):`, error);
    }
  }

  /**
   * Retrieves data from session storage by key
   * @param key The storage key
   * @returns The parsed value or null if not found
   */
  getItem<T>(key: string): T | null {
    try {
      const data = sessionStorage.getItem(key);
      if (data) {
        return JSON.parse(data) as T;
      }
    } catch (error) {
      console.error(`Failed to retrieve item from session storage (key: ${key}):`, error);
    }
    return null;
  }

  /**
   * Retrieves a raw string value from session storage without JSON parsing
   * @param key The storage key
   * @returns The raw string value or null if not found
   */
  getRawItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.error(`Failed to retrieve raw item from session storage (key: ${key}):`, error);
      return null;
    }
  }

  /**
   * Removes a specific item from session storage
   * @param key The storage key to remove
   */
  removeItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove item from session storage (key: ${key}):`, error);
    }
  }

  /**
   * Clears all data from session storage
   * Use this for a complete session reset (e.g., on logout)
   */
  clear(): void {
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Failed to clear session storage:', error);
    }
  }

  /**
   * Checks if a key exists in session storage
   * @param key The storage key to check
   * @returns true if the key exists, false otherwise
   */
  hasItem(key: string): boolean {
    return sessionStorage.getItem(key) !== null;
  }

  /**
   * Gets all keys currently stored in session storage
   * @returns Array of all storage keys
   */
  getAllKeys(): string[] {
    try {
      const keys: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      console.error('Failed to get all keys from session storage:', error);
      return [];
    }
  }

  /**
   * Removes multiple items from session storage by keys
   * @param keys Array of storage keys to remove
   */
  removeItems(keys: string[]): void {
    keys.forEach(key => this.removeItem(key));
  }
}
